import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Layout, Modal, Tooltip, Button, Card, Divider } from 'antd';
import { FontSizeOutlined } from '@ant-design/icons';
import { API } from '../../config';
import { useAuth } from '../../auth';
import logo from '../../asset/logo/large-white.svg';
import './App.css';
import { EnglishToHebrew, HEBREW_PREFIXES } from '../Populate/HebrewInput';
import { EnglishToGreek, GREEK_PREFIXES, GREEK_SECOND_PREFIXES } from '../Populate/GreekInput';
import { parseWordCharacters, getTypeColor, getTypeLabel, getTransliteration } from '../Populate/characterInfo';
import { KeyboardReference } from '../Populate/KeyboardReference';

const { Header, Content, Footer } = Layout;

interface ManuscriptWord {
  chapter: number;
  verse: number;
  position: number;
  word: string;
  is_punctuation: number;
}

type DemoTab = 'hebrew' | 'greek';

// Strip diacritics/vowel points for comparison
function stripDiacritics(str: string): string {
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[\u0591-\u05C7]/g, '')
    .replace(/[\u0300-\u0362]/g, '');
}

function App() {
  const { isAuthenticated, user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState<DemoTab>('hebrew');
  const [words, setWords] = useState<ManuscriptWord[]>([]);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [userInput, setUserInput] = useState('');
  const [completedWords, setCompletedWords] = useState<Set<number>>(new Set());
  const [isLoading, setIsLoading] = useState(true);

  // Character analysis modal state
  const [charAnalysisWord, setCharAnalysisWord] = useState<string | null>(null);
  const [isCharModalVisible, setIsCharModalVisible] = useState<boolean>(false);
  const [showKeyboard, setShowKeyboard] = useState<boolean>(false);

  // Refs for handling prefix key combinations
  const hebrewPendingRef = useRef<string>('');
  const greekPendingRef = useRef<string>('');
  const inputRef = useRef<HTMLInputElement>(null);
  const cursorPositionRef = useRef<{ start: number; end: number }>({ start: 0, end: 0 });

  // Fetch demo data
  useEffect(() => {
    const fetchDemoData = async () => {
      setIsLoading(true);
      try {
        const book = activeTab === 'hebrew' ? 'genesis' : '3john';
        const response = await axios.get<ManuscriptWord[]>(API.demoBible(book, 1));
        let nonPunctuation = response.data.filter(w => w.is_punctuation === 0);
        if (activeTab === 'hebrew') {
          nonPunctuation = nonPunctuation.filter(w => w.verse === 1);
        }
        setWords(nonPunctuation);
        setCurrentWordIndex(0);
        setUserInput('');
        setCompletedWords(new Set());
      } catch (error) {
        console.error('Failed to fetch demo data:', error);
        if (activeTab === 'hebrew') {
          setWords([
            { chapter: 1, verse: 1, position: 1, word: 'בְּרֵאשִׁית', is_punctuation: 0 },
            { chapter: 1, verse: 1, position: 2, word: 'בָּרָא', is_punctuation: 0 },
            { chapter: 1, verse: 1, position: 3, word: 'אֱלֹהִים', is_punctuation: 0 },
          ]);
        } else {
          setWords([
            { chapter: 1, verse: 1, position: 1, word: 'Ὁ', is_punctuation: 0 },
            { chapter: 1, verse: 1, position: 2, word: 'πρεσβύτερος', is_punctuation: 0 },
            { chapter: 1, verse: 1, position: 3, word: 'Γαΐῳ', is_punctuation: 0 },
          ]);
        }
      }
      setIsLoading(false);
    };

    fetchDemoData();
    hebrewPendingRef.current = '';
    greekPendingRef.current = '';
  }, [activeTab]);

  // Check if input matches the current word
  const checkMatch = useCallback((inputValue: string) => {
    if (words.length === 0) return;

    const currentWord = words[currentWordIndex];
    const inputStripped = stripDiacritics(inputValue).toLowerCase();
    const targetStripped = stripDiacritics(currentWord.word).toLowerCase();

    if (inputStripped === targetStripped) {
      setCompletedWords(prev => new Set(Array.from(prev).concat(currentWordIndex)));
      if (currentWordIndex < words.length - 1) {
        setCurrentWordIndex(prev => prev + 1);
        setUserInput('');
      }
    }
  }, [words, currentWordIndex]);

  // Handle keydown for prefix characters (Hebrew)
  const handleHebrewKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    const key = e.key;

    if (['Shift', 'Control', 'Alt', 'Meta', 'CapsLock'].includes(key)) {
      return;
    }

    if (HEBREW_PREFIXES.includes(key)) {
      e.preventDefault();
      hebrewPendingRef.current = key;
      return;
    }

    const pending = hebrewPendingRef.current;
    if (pending) {
      e.preventDefault();
      hebrewPendingRef.current = '';
      const input = e.currentTarget;
      const start = input.selectionStart || 0;
      const end = input.selectionEnd || 0;
      const combo = pending + key;
      const before = userInput.slice(0, start);
      const after = userInput.slice(end);
      const newText = before + combo + after;
      const converted = EnglishToHebrew(newText);
      setUserInput(converted);
      checkMatch(converted);
      setTimeout(() => {
        const newPos = before.length + 1;
        input.setSelectionRange(newPos, newPos);
      }, 0);
      return;
    }
  }, [userInput, checkMatch]);

  // Handle keydown for prefix characters (Greek)
  const handleGreekKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    const key = e.key;

    if (['Shift', 'Control', 'Alt', 'Meta', 'CapsLock'].includes(key)) {
      return;
    }

    if (GREEK_PREFIXES.includes(key) && !greekPendingRef.current) {
      e.preventDefault();
      greekPendingRef.current = key;
      return;
    }

    const pending = greekPendingRef.current;
    if (pending) {
      if (GREEK_SECOND_PREFIXES.includes(key)) {
        e.preventDefault();
        greekPendingRef.current = pending + key;
        return;
      }

      e.preventDefault();
      greekPendingRef.current = '';
      const input = e.currentTarget;
      const start = input.selectionStart || 0;
      const end = input.selectionEnd || 0;
      const combo = pending + key;
      const before = userInput.slice(0, start);
      const after = userInput.slice(end);
      const newText = before + combo + after;
      const converted = EnglishToGreek(newText);
      setUserInput(converted);
      checkMatch(converted);
      setTimeout(() => {
        const newPos = before.length + 1;
        input.setSelectionRange(newPos, newPos);
      }, 0);
      return;
    }
  }, [userInput, checkMatch]);

  // Handle normal input changes
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const converted = activeTab === 'hebrew'
      ? EnglishToHebrew(rawValue)
      : EnglishToGreek(rawValue);

    setUserInput(converted);
    checkMatch(converted);
  }, [activeTab, checkMatch]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (activeTab === 'hebrew') {
      handleHebrewKeyDown(e);
    } else {
      handleGreekKeyDown(e);
    }
  }, [activeTab, handleHebrewKeyDown, handleGreekKeyDown]);

  // Handle keyboard reference character clicks
  const handleKeyboardCharacterClick = useCallback((char: string) => {
    if (!inputRef.current) return;

    const input = inputRef.current;
    const start = input.selectionStart || cursorPositionRef.current.start;
    const end = input.selectionEnd || cursorPositionRef.current.end;

    const before = userInput.slice(0, start);
    const after = userInput.slice(end);
    const newValue = before + char + after;

    setUserInput(newValue);
    checkMatch(newValue);

    // Update cursor position for next insertion
    const newPos = start + char.length;
    cursorPositionRef.current = { start: newPos, end: newPos };

    // Refocus and set cursor
    setTimeout(() => {
      input.focus();
      input.setSelectionRange(newPos, newPos);
    }, 0);
  }, [userInput, checkMatch]);

  const handleWordClick = (word: string) => {
    setCharAnalysisWord(word);
    setIsCharModalVisible(true);
  };

  const handleWordSelect = (index: number) => {
    if (!completedWords.has(index)) {
      setCurrentWordIndex(index);
      setUserInput('');
      inputRef.current?.focus();
    }
  };

  const currentWord = words[currentWordIndex];
  const lang = activeTab;

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Header - matches edit mode */}
      <Header className="App-Header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: 100 }}>
        <img src={logo} alt="NBBA" style={{ height: 75 }} />
        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          {isAuthenticated ? (
            <>
              <span style={{ color: 'rgba(255,255,255,0.85)' }}>Welcome, {user?.username}</span>
              <Link to="/populate">
                <Button type="primary">Go to App</Button>
              </Link>
              <Button onClick={logout}>Logout</Button>
            </>
          ) : (
            <>
              <Link to="/login">
                <Button>Log In</Button>
              </Link>
              <Link to="/signup">
                <Button type="primary">Sign Up</Button>
              </Link>
            </>
          )}
        </div>
      </Header>

      <Content style={{ padding: '50px 50px' }}>
        {/* Hero text */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <h1 style={{ marginBottom: 8 }}>
            <span style={{
              fontFamily: 'Geometric231, serif',
              fontSize: 48,
              color: '#2c1810',
              letterSpacing: 1
            }}>
              NotByBread
            </span>
            <span style={{
              fontFamily: 'Kallinsha, serif',
              fontSize: 52,
              color: '#2c1810'
            }}>
              Alone
            </span>
          </h1>
          <p style={{
            fontSize: 18,
            color: '#5a4a3a',
            marginBottom: 16,
            fontStyle: 'italic'
          }}>
            Before the printing press, scribes learned Scripture by copying it word by word. Now it's your turn.
          </p>
          <p style={{ color: '#666', maxWidth: 600, margin: '0 auto', fontSize: 14 }}>
            Free and open-source tools for mastering Biblical Hebrew, Aramaic, Greek, and Latin. Type it. Parse it. Speak it. Internalize it.
          </p>
          {!isAuthenticated && (
            <div style={{ marginTop: 20, display: 'flex', gap: 12, justifyContent: 'center' }}>
              <Link to="/signup">
                <Button type="primary" size="large">Get Started Free</Button>
              </Link>
              <Link to="/login">
                <Button size="large">Log In</Button>
              </Link>
            </div>
          )}
        </div>

        <Divider>Interactive Demo</Divider>

        {/* Tab Buttons */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
          <Button
            type={activeTab === 'hebrew' ? 'primary' : 'default'}
            onClick={() => setActiveTab('hebrew')}
            style={{ borderRadius: '6px 0 0 6px' }}
          >
            Hebrew (Genesis 1:1)
          </Button>
          <Button
            type={activeTab === 'greek' ? 'primary' : 'default'}
            onClick={() => setActiveTab('greek')}
            style={{ borderRadius: '0 6px 6px 0' }}
          >
            Greek (3 John 1:1)
          </Button>
        </div>

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: 50, color: '#888' }}>Loading...</div>
        ) : (
          <>
            {/* Exemplar - parchment style */}
            <div style={{
              padding: '20px 24px',
              background: 'linear-gradient(135deg, #F2EFE4 0%, #F5EFE4 50%, #F2E8D5 100%)',
              border: '2px solid #c9b896',
              borderRadius: 8,
              boxShadow: 'inset 0 0 30px rgba(139, 119, 82, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1)',
              position: 'relative',
              marginBottom: 24
            }}>
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                background: 'linear-gradient(to bottom, rgba(139, 119, 82, 0.2), transparent)',
                borderRadius: '8px 8px 0 0'
              }} />
              <div style={{
                fontSize: 11,
                color: '#8b7752',
                marginBottom: 12,
                textTransform: 'uppercase',
                letterSpacing: 2,
                fontWeight: 600,
                borderBottom: '1px solid #d4c4a8',
                paddingBottom: 8
              }}>
                Exemplar - {activeTab === 'hebrew' ? 'Genesis 1:1' : '3 John 1:1'}
              </div>

              <div
                className={activeTab === 'hebrew' ? 'hebrew hebrew-content' : 'greek greek-content'}
                style={{
                  fontSize: activeTab === 'hebrew' ? 'clamp(24px, 6vw, 40px)' : 'clamp(20px, 5vw, 30px)',
                  lineHeight: 1.8,
                  color: '#3d3425',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  textAlign: 'center',
                  fontFamily: activeTab === 'hebrew' ? 'TaameyAshkenaz, EzraHebrew, serif' : undefined
                }}
              >
                <sup style={{ fontSize: 12, marginRight: 4, color: '#8b7752' }}><b>1</b></sup>
                {words.map((word, index) => {
                  const isCompleted = completedWords.has(index);
                  const isCurrent = index === currentWordIndex && !isCompleted;
                  const thisEndsMaqaf = word.word.endsWith('־');

                  return (
                    <Tooltip key={`${word.verse}-${word.position}`} title="Click to see character breakdown">
                      <span
                        onClick={() => handleWordClick(word.word)}
                        onDoubleClick={() => handleWordSelect(index)}
                        style={{
                          backgroundColor: isCurrent ? '#fff4d9' : isCompleted ? 'rgba(34, 197, 94, 0.15)' : 'transparent',
                          padding: '2px 6px',
                          marginInlineEnd: thisEndsMaqaf ? '-0.3em' : undefined,
                          borderRadius: 3,
                          border: isCurrent ? '2px solid #d4a84b' : isCompleted ? '2px solid #22c55e' : 'none',
                          boxShadow: isCurrent ? '0 0 8px rgba(212, 168, 75, 0.4)' : 'none',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          color: isCompleted ? '#15803d' : '#3d3425'
                        }}
                      >
                        {word.word}
                      </span>
                    </Tooltip>
                  );
                })}
              </div>

              <div style={{ marginTop: 12, fontSize: 10, color: '#a89870', borderTop: '1px solid #d4c4a8', paddingTop: 10 }}>
                {activeTab === 'hebrew' ? 'Source: Westminster Leningrad Codex (Public Domain)' : "Source: Tischendorf's 8th Edition (Public Domain)"}
                {' '}<span style={{ marginLeft: 8 }}>Click a word to see letter breakdown. Double-click to select.</span>
              </div>
            </div>

            {/* Input Section - Card style like edit mode */}
            <Card title="Type the Word" size="small">
              {currentWord && !completedWords.has(currentWordIndex) ? (
                <>
                  <div style={{ textAlign: 'center', marginBottom: 16 }}>
                    <div
                      className={activeTab === 'hebrew' ? 'hebrew' : 'greek'}
                      style={{
                        fontSize: activeTab === 'hebrew' ? 48 : 36,
                        fontFamily: activeTab === 'hebrew' ? 'TaameyAshkenaz, EzraHebrew, serif' : 'TekniaGreek, serif',
                        color: '#333'
                      }}
                    >
                      {currentWord.word}
                    </div>
                  </div>

                  <div style={{ maxWidth: 400, margin: '0 auto' }}>
                    <input
                      ref={inputRef}
                      type="text"
                      value={userInput}
                      onChange={handleInputChange}
                      onKeyDown={handleKeyDown}
                      onSelect={(e) => {
                        const target = e.target as HTMLInputElement;
                        cursorPositionRef.current = {
                          start: target.selectionStart || 0,
                          end: target.selectionEnd || 0
                        };
                      }}
                      placeholder="Start typing..."
                      className={activeTab === 'hebrew' ? 'hebrew' : 'greek'}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '1px solid #d9d9d9',
                        borderRadius: 6,
                        fontSize: 24,
                        textAlign: 'center',
                        fontFamily: activeTab === 'hebrew' ? 'TaameyAshkenaz, EzraHebrew, serif' : 'TekniaGreek, serif',
                        direction: activeTab === 'hebrew' ? 'rtl' : 'ltr',
                        outline: 'none'
                      }}
                      autoFocus
                      autoComplete="off"
                    />
                    <p style={{ textAlign: 'center', color: '#888', fontSize: 12, marginTop: 8 }}>
                      No special keyboard needed - just type normally!
                    </p>
                  </div>
                </>
              ) : completedWords.size === words.length && words.length > 0 ? (
                <div style={{ textAlign: 'center', padding: 24 }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>📜 ✨</div>
                  <div style={{ fontSize: 18, fontWeight: 600, color: '#15803d', marginBottom: 8 }}>
                    Great job! You completed the demo!
                  </div>
                  {!isAuthenticated && (
                    <Link to="/signup">
                      <Button type="primary" size="large" style={{ marginTop: 8 }}>
                        Sign Up for Full Experience
                      </Button>
                    </Link>
                  )}
                </div>
              ) : null}

              {/* Progress */}
              <div style={{ marginTop: 20, textAlign: 'center' }}>
                <span style={{ color: '#666', fontSize: 13 }}>
                  Progress: {completedWords.size} / {words.length} words
                </span>
                <div style={{
                  width: '100%',
                  maxWidth: 400,
                  margin: '8px auto 0',
                  background: '#e8e8e8',
                  borderRadius: 9999,
                  height: 8
                }}>
                  <div
                    style={{
                      width: `${(completedWords.size / Math.max(words.length, 1)) * 100}%`,
                      background: '#52c41a',
                      height: 8,
                      borderRadius: 9999,
                      transition: 'width 0.3s'
                    }}
                  />
                </div>
              </div>

              {/* Keyboard Reference */}
              <div style={{ marginTop: 16, marginBottom: 12, textAlign: 'right' }}>
                <Button
                  type={showKeyboard ? 'primary' : 'default'}
                  icon={<FontSizeOutlined />}
                  onClick={() => setShowKeyboard(!showKeyboard)}
                  size="small"
                >
                  {showKeyboard ? 'Hide' : 'Show'} Keyboard Reference
                </Button>
              </div>
              {showKeyboard && (
                <KeyboardReference
                  lang={activeTab}
                  onClose={() => setShowKeyboard(false)}
                  onCharacterClick={handleKeyboardCharacterClick}
                />
              )}
            </Card>

            {/* Features */}
            <Divider>Features</Divider>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 16 }}>
              <Card size="small">
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 24, marginBottom: 8 }}>⌨️</div>
                  <h3 style={{ margin: '0 0 8px 0' }}>No Special Keyboard</h3>
                  <p style={{ color: '#666', margin: 0, fontSize: 13 }}>
                    Type Greek and Hebrew with your regular keyboard - automatic conversion as you type
                  </p>
                </div>
              </Card>
              <Card size="small">
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 24, marginBottom: 8 }}>🎙️</div>
                  <h3 style={{ margin: '0 0 8px 0' }}>Audio Recording</h3>
                  <p style={{ color: '#666', margin: 0, fontSize: 13 }}>
                    Record your pronunciation for each word and track your progress
                  </p>
                </div>
              </Card>
              <Card size="small">
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 24, marginBottom: 8 }}>📊</div>
                  <h3 style={{ margin: '0 0 8px 0' }}>Personal Progress</h3>
                  <p style={{ color: '#666', margin: 0, fontSize: 13 }}>
                    Your learning data is saved and synced across devices
                  </p>
                </div>
              </Card>
            </div>
          </>
        )}
      </Content>

      <Footer style={{ textAlign: 'center' }}>
        NotByBreadAlone.io |{' '}
        <a href="mailto:notbybreadalone.io@gmail.com">Contact</a>
      </Footer>

      {/* Character Analysis Modal */}
      <Modal
        title={
          <div style={{ textAlign: 'center' }}>
            <span style={{
              fontSize: '28px',
              fontFamily: lang === 'hebrew' ? 'TaameyAshkenaz, serif' : 'SBL Greek, Gentium Plus, serif',
              direction: lang === 'hebrew' ? 'rtl' : 'ltr'
            }}>
              {charAnalysisWord}
            </span>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
              Character Breakdown
            </div>
          </div>
        }
        open={isCharModalVisible}
        onCancel={() => {
          setIsCharModalVisible(false);
          setCharAnalysisWord(null);
        }}
        footer={null}
        width={600}
        destroyOnClose
      >
        {charAnalysisWord && (
          <div style={{
            background: 'linear-gradient(135deg, #f5f0e6 0%, #e8dcc8 50%, #f2ebe0 100%)',
            border: '2px solid #c4a35a',
            borderRadius: '8px',
            padding: '20px',
            boxShadow: 'inset 0 2px 8px rgba(139, 90, 43, 0.15)'
          }}>
            {/* Legend */}
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '12px',
              marginBottom: '16px',
              padding: '10px',
              background: 'rgba(255,255,255,0.5)',
              borderRadius: '6px',
              fontSize: '11px'
            }}>
              {(['consonant', 'vowel', 'cantillation', 'punctuation', 'breathing', 'accent', 'other'] as const).map(type => (
                <span key={type} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '3px',
                    background: getTypeColor(type),
                    display: 'inline-block'
                  }}></span>
                  {getTypeLabel(type)}
                </span>
              ))}
            </div>

            {/* Character breakdown */}
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px',
              direction: lang === 'hebrew' ? 'rtl' : 'ltr',
              justifyContent: 'center'
            }}>
              {parseWordCharacters(charAnalysisWord, lang as 'hebrew' | 'greek').map((charInfo, idx) => (
                <div
                  key={idx}
                  style={{
                    background: 'white',
                    border: `2px solid ${getTypeColor(charInfo.type)}`,
                    borderRadius: '8px',
                    padding: '12px 16px',
                    minWidth: '90px',
                    textAlign: 'center',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                >
                  <div style={{
                    fontSize: '36px',
                    fontFamily: lang === 'hebrew' ? 'TaameyAshkenaz, serif' : 'SBL Greek, Gentium Plus, serif',
                    lineHeight: 1.2,
                    color: '#3d2914'
                  }}>
                    {charInfo.char}
                  </div>
                  <div style={{
                    background: getTypeColor(charInfo.type),
                    color: 'white',
                    fontSize: '9px',
                    padding: '2px 6px',
                    borderRadius: '10px',
                    marginTop: '4px',
                    display: 'inline-block'
                  }}>
                    {getTypeLabel(charInfo.type)}
                  </div>
                  <div style={{
                    fontSize: '12px',
                    fontWeight: 'bold',
                    color: '#5a4a3a',
                    marginTop: '8px'
                  }}>
                    {charInfo.name}
                  </div>
                  {charInfo.phonetic && (
                    <div style={{
                      fontSize: '11px',
                      color: '#2e7d32',
                      fontStyle: 'italic',
                      marginTop: '4px'
                    }}>
                      /{charInfo.phonetic}/
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Transliteration */}
            <div style={{
              marginTop: '20px',
              padding: '12px',
              background: 'rgba(255,255,255,0.6)',
              borderRadius: '6px',
              fontSize: '14px',
              color: '#5a4a3a'
            }}>
              <strong>Transliteration:</strong>{' '}
              <span style={{ fontFamily: 'serif', fontSize: '16px', letterSpacing: '0.5px' }}>
                {getTransliteration(charAnalysisWord, lang as 'hebrew' | 'greek') || 'N/A'}
              </span>
            </div>

            {/* Pronunciation guide */}
            <div style={{
              marginTop: '10px',
              padding: '12px',
              background: 'rgba(255,255,255,0.6)',
              borderRadius: '6px',
              fontSize: '12px',
              color: '#5a4a3a'
            }}>
              <strong>Pronunciation Guide:</strong>{' '}
              {parseWordCharacters(charAnalysisWord, lang as 'hebrew' | 'greek')
                .filter(c => c.phonetic)
                .map(c => c.phonetic)
                .join('-') || 'No phonetic data available'}
            </div>
          </div>
        )}
      </Modal>
    </Layout>
  );
}

export default App;
